import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWindowSize } from '@app/hooks';

import { useLeadsRegistryCardsVirtualScroll } from '../hooks/useLeadsRegistryCardsVirtualScroll';

import { LeadsRegistryLeadCard } from './LeadsRegistryLeadCard';
import { LeadsRegistryLeadCardSkeleton } from './LeadsRegistryLeadCardSkeleton';

import type { LeadsRegistryTableRow } from '../../../../model/types';
import type { CSSProperties, FC, UIEvent } from 'react';

import './LeadsRegistryCards.scss';

const LEADS_CARD_ROW_HEIGHT = 180;
const LEADS_CARD_ROW_HEIGHT_COMPACT = 136;
const LEADS_CARD_OVERSCAN = 6;
const LEADS_CARD_LOAD_MORE_THRESHOLD = 240;
const MOBILE_COMPACT_WIDTH = 400;
const FALLBACK_VIEWPORT_HEIGHT = 320;

export interface LeadsRegistryCardsProps {
  hasMoreLeads: boolean;
  isLoadingMore: boolean;
  rows: LeadsRegistryTableRow[];
  onLoadMore(): void;
}

const getRowKey = (row: LeadsRegistryTableRow) =>
  row.kind === 'lead' ? row.id : row.skeletonId;

const getContainerStyle = (rowHeight: number): CSSProperties =>
  ({
    '--leads-registry-card-row-height': `${rowHeight}px`,
  } as CSSProperties);

export const LeadsRegistryCards: FC<LeadsRegistryCardsProps> = ({
  hasMoreLeads,
  isLoadingMore,
  onLoadMore,
  rows,
}) => {
  const { innerHeight, innerWidth } = useWindowSize();
  const isCompact = innerWidth <= MOBILE_COMPACT_WIDTH;
  const rowHeight = isCompact
    ? LEADS_CARD_ROW_HEIGHT_COMPACT
    : LEADS_CARD_ROW_HEIGHT;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollViewportHeight, setScrollViewportHeight] = useState(
    Math.max(FALLBACK_VIEWPORT_HEIGHT, innerHeight - 260)
  );
  const lastLoadMoreRowsCountRef = useRef<number | null>(null);
  const { range, updateScrollTop, visibleItems } =
    useLeadsRegistryCardsVirtualScroll({
      itemHeight: rowHeight,
      items: rows,
      overscan: LEADS_CARD_OVERSCAN,
      viewportHeight: scrollViewportHeight,
    });

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) return;

    const updateViewportHeight = () => {
      setScrollViewportHeight((prev) => {
        const next = scrollContainer.clientHeight;

        return prev === next ? prev : next;
      });
    };

    updateViewportHeight();

    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(scrollContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isLoadingMore) return;

    lastLoadMoreRowsCountRef.current = null;
  }, [isLoadingMore, rows.length]);

  const scrollHandler = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      updateScrollTop(scrollTop);

      if (isLoadingMore || !hasMoreLeads) {
        return;
      }

      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      if (
        distanceToBottom <= LEADS_CARD_LOAD_MORE_THRESHOLD &&
        lastLoadMoreRowsCountRef.current !== rows.length
      ) {
        lastLoadMoreRowsCountRef.current = rows.length;
        onLoadMore();
      }
    },
    [hasMoreLeads, isLoadingMore, onLoadMore, rows.length, updateScrollTop]
  );

  const spacerStyle = useMemo<CSSProperties>(
    () => ({
      height: range.totalHeight,
    }),
    [range.totalHeight]
  );

  const visibleItemsStyle = useMemo<CSSProperties>(
    () => ({
      transform: `translateY(${range.offsetTop}px)`,
    }),
    [range.offsetTop]
  );

  return (
    <div className="LeadsRegistryCards" style={getContainerStyle(rowHeight)}>
      <div
        className="LeadsRegistryCards__scroll"
        ref={scrollContainerRef}
        onScroll={scrollHandler}
      >
        {rows.length === 0 ? (
          <div className="LeadsRegistryCards__empty">Лиды не найдены</div>
        ) : (
          <div className="LeadsRegistryCards__body" style={spacerStyle}>
            <div
              className="LeadsRegistryCards__placeholder-layer"
              aria-hidden="true"
            />
            <div
              className="LeadsRegistryCards__visible-items"
              style={visibleItemsStyle}
            >
              {visibleItems.map((row) => (
                <div className="LeadsRegistryCards__item" key={getRowKey(row)}>
                  {row.kind === 'lead' ? (
                    <LeadsRegistryLeadCard row={row} isCompact={isCompact} />
                  ) : (
                    <LeadsRegistryLeadCardSkeleton isCompact={isCompact} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
